import { useState } from 'react'
import { useEducation, useCreateEducation, useUpdateEducation, useDeleteEducation } from '@/hooks/useEducation'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { EmptyState } from '@/components/profile/EmptyState'
import { GraduationCap, Plus, Pencil, Trash2 } from 'lucide-react'

const LEVELS = [
  { value: 'elementary', label: 'Elementary', color: 'bg-blue-100 text-blue-800' },
  { value: 'secondary', label: 'Secondary', color: 'bg-green-100 text-green-800' },
  { value: 'senior_high', label: 'Senior High', color: 'bg-teal-100 text-teal-800' },
  { value: 'vocational', label: 'Vocational', color: 'bg-orange-100 text-orange-800' },
  { value: 'college', label: 'College', color: 'bg-purple-100 text-purple-800' },
  { value: 'post_graduate', label: 'Post-Graduate', color: 'bg-pink-100 text-pink-800' },
]
const STATUSES = ['completed', 'graduated', 'undergraduate', 'in_progress', 'dropped_out']
const COURSE_LEVELS = ['vocational', 'college', 'post_graduate']

type FormData = { id?: string; level: string; status: string; school_name: string; course: string; year_started: string; year_ended: string; honors: string; notes: string }
const empty: FormData = { level: '', status: '', school_name: '', course: '', year_started: '', year_ended: '', honors: '', notes: '' }

export function EducationPage() {
  const { data: items = [], isLoading } = useEducation()
  const create = useCreateEducation()
  const updateEd = useUpdateEducation()
  const deleteEd = useDeleteEducation()
  const [form, setForm] = useState<FormData | null>(null)
  const [formError, setFormError] = useState<string | null>(null)

  const openEdit = (item: Record<string, unknown>) => setForm({
    id: item.id as string, level: item.level as string, status: item.status as string,
    school_name: (item.school_name as string) ?? '', course: (item.course as string) ?? '',
    year_started: item.year_started ? String(item.year_started) : '', year_ended: item.year_ended ? String(item.year_ended) : '',
    honors: (item.honors as string) ?? '', notes: (item.notes as string) ?? '',
  })

  const handleSave = () => {
    if (!form || !form.level || !form.status) { setFormError('Level and status are required'); return }
    const ys = form.year_started ? Number(form.year_started) : null
    const ye = form.year_ended ? Number(form.year_ended) : null
    if (ys && ye && ye < ys) { setFormError('Year ended must be ≥ year started'); return }
    setFormError(null)
    const payload = {
      level: form.level, status: form.status, school_name: form.school_name || null,
      course: COURSE_LEVELS.includes(form.level) ? form.course || null : null,
      year_started: ys, year_ended: ye, honors: form.honors || null, notes: form.notes || null,
    }
    if (form.id) {
      updateEd.mutate({ id: form.id, ...payload }, { onSuccess: () => setForm(null) })
    } else {
      create.mutate(payload, { onSuccess: () => setForm(null) })
    }
  }

  if (isLoading) return <div className="space-y-3">{[1, 2, 3].map((i) => <div key={i} className="h-20 animate-pulse rounded-lg bg-muted" />)}</div>

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => setForm({ ...empty })}><Plus className="h-3 w-3" /> Add Education</Button>
      </div>

      {items.length === 0 ? (
        <EmptyState icon={GraduationCap} title="No education entries yet" description="Add your educational background." actionLabel="Add Education" onAction={() => setForm({ ...empty })} />
      ) : items.map((item) => {
        const lvl = LEVELS.find((l) => l.value === item.level)
        return (
          <Card key={item.id}>
            <CardContent className="flex items-start justify-between pt-4">
              <div>
                <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-medium ${lvl?.color ?? 'bg-muted'}`}>{lvl?.label ?? item.level}</span>
                {item.school_name && <p className="mt-1 font-medium text-sm">{item.school_name}</p>}
                {item.course && <p className="text-xs text-muted-foreground">{item.course}</p>}
                <p className="text-xs text-muted-foreground">{item.year_started ?? '?'} – {item.year_ended ?? 'Present'} · {item.status?.replace(/_/g, ' ')}</p>
                {item.honors && <p className="text-xs italic text-muted-foreground">{item.honors}</p>}
              </div>
              <div className="flex gap-1">
                <Button size="icon" variant="ghost" onClick={() => openEdit(item)}><Pencil className="h-3.5 w-3.5" /></Button>
                <Button size="icon" variant="ghost" onClick={() => deleteEd.mutate(item.id)}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
              </div>
            </CardContent>
          </Card>
        )
      })}

      {form && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setForm(null)}>
          <div className="w-full max-w-sm max-h-[90vh] overflow-y-auto rounded-lg bg-background p-6 shadow-lg" onClick={(e) => e.stopPropagation()}>
            <h3 className="mb-3 font-semibold">{form.id ? 'Edit' : 'Add'} Education</h3>
            {formError && <p className="mb-2 text-xs text-red-600">{formError}</p>}
            <div className="space-y-3">
              <div className="space-y-1">
                <Label>Level *</Label>
                <Select value={form.level} onValueChange={(v) => setForm({ ...form, level: v })}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{LEVELS.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="space-y-1">
                <Label>Status *</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, ' ')}</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="space-y-1"><Label>School Name</Label><Input value={form.school_name} onChange={(e) => setForm({ ...form, school_name: e.target.value })} /></div>
              {COURSE_LEVELS.includes(form.level) && <div className="space-y-1"><Label>Course</Label><Input value={form.course} onChange={(e) => setForm({ ...form, course: e.target.value })} /></div>}
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1"><Label>Year Started</Label><Input type="number" value={form.year_started} onChange={(e) => setForm({ ...form, year_started: e.target.value })} /></div>
                <div className="space-y-1"><Label>Year Ended</Label><Input type="number" value={form.year_ended} onChange={(e) => setForm({ ...form, year_ended: e.target.value })} /></div>
              </div>
              <div className="space-y-1"><Label>Honors</Label><Input value={form.honors} onChange={(e) => setForm({ ...form, honors: e.target.value })} /></div>
              <Button className="w-full" onClick={handleSave} disabled={create.isPending || updateEd.isPending}>Save</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
